/**
 * Helpers for FolderThirdParties factory methods
 */

import type { IBaseNotary } from "../../BaseNotaryFactory";
import type { IThirdParty, IAddThirdPartyRequest, IThirdPartyAutocompleteResult, IThirdPartyAutocompleteOptions } from "../FolderThirdParties";
import { buildEmptyPayload } from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";
import { appendActiveOfficeUidSearchParam } from "@Front/Api/helpers/activeOfficeUidQueryParamHelpers";

/**
 * Create add third party method
 */
export function createAddThirdPartyMethod(baseNotary: IBaseNotary) {
	return async (folderUid: string, data: IAddThirdPartyRequest): Promise<IThirdParty> => {
		const url = new URL(baseNotary.namespaceUrl.concat(`/folders/${folderUid}/third-parties`));
		try {
			return await baseNotary.postRequest<IThirdParty>(url, data);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create get third parties method
 */
export function createGetThirdPartiesMethod(baseNotary: IBaseNotary) {
	return async (folderUid: string): Promise<IThirdParty[]> => {
		const url = new URL(baseNotary.namespaceUrl.concat(`/folders/${folderUid}/third-parties`));
		try {
			return await baseNotary.getRequest<IThirdParty[]>(url);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create get third party method
 */
export function createGetThirdPartyMethod(baseNotary: IBaseNotary) {
	return async (uid: string): Promise<IThirdParty> => {
		const url = new URL(baseNotary.namespaceUrl.concat(`/third-parties/${uid}`));
		try {
			return await baseNotary.getRequest<IThirdParty>(url);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create update third party method
 */
export function createUpdateThirdPartyMethod(baseNotary: IBaseNotary) {
	return async (
		uid: string,
		data: {
			first_name?: string | null;
			last_name?: string | null;
			email?: string | null;
			phone?: string | null;
			role_description?: string | null;
			company_name?: string | null;
		},
	): Promise<IThirdParty> => {
		const url = new URL(baseNotary.namespaceUrl.concat(`/third-parties/${uid}`));
		try {
			return await baseNotary.putRequest<IThirdParty>(url, data);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create remove third party method
 */
export function createRemoveThirdPartyMethod(baseNotary: IBaseNotary) {
	return async (uid: string): Promise<void> => {
		const url = new URL(baseNotary.namespaceUrl.concat(`/third-parties/${uid}`));
		try {
			return await baseNotary.deleteRequest<void>(url);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create resend code method
 */
export function createResendCodeMethod(baseNotary: IBaseNotary) {
	return async (uid: string): Promise<{ message: string; emailQueued?: boolean; code?: string }> => {
		const url = new URL(baseNotary.namespaceUrl.concat(`/third-parties/${uid}/resend-code`));
		try {
			return await baseNotary.postRequest<{ message: string; emailQueued?: boolean; code?: string }>(url, buildEmptyPayload());
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create autocomplete method
 */
export function createAutocompleteMethod(baseNotary: IBaseNotary) {
	return async (searchTerm: string, options?: IThirdPartyAutocompleteOptions): Promise<IThirdPartyAutocompleteResult[]> => {
		const { limit = 10, roleType, allOffices = false, notaryScopeOfficeUid } = options ?? {};
		const url = new URL(baseNotary.namespaceUrl.concat(`/folders/autocomplete/third-parties`));
		url.searchParams.set("search", searchTerm);
		url.searchParams.set("limit", limit.toString());
		if (roleType) {
			url.searchParams.set("role_type", roleType);
		}
		if (allOffices) {
			url.searchParams.set("all_offices", "true");
		} else if (notaryScopeOfficeUid) {
			appendActiveOfficeUidSearchParam(url, notaryScopeOfficeUid);
		}
		try {
			return await baseNotary.getRequest<IThirdPartyAutocompleteResult[]>(url);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}
